import type { Role, UserStatus } from '../../types';

/**
 * How roles are described to an administrator.
 *
 * "Salesman" is the word Salestrendz uses in the product; `sales_rep` is
 * the word the database uses. They are kept apart on purpose — renaming
 * the label is a copy change, renaming the enum is a migration.
 */
export const ROLE_LABEL: Record<Role, string> = {
  platform_admin: 'Platform admin',
  company_admin: 'Admin',
  manager: 'Manager',
  sales_rep: 'Salesman',
  delivery: 'Delivery',
  distributor: 'Distributor',
};

export const ROLE_BLURB: Record<Role, string> = {
  platform_admin: 'Salestrendz staff. Not something you can assign.',
  company_admin:
    'Runs the account from this console — the customer form, areas, the team, '
    + 'and billing. Signs in with an email address and password.',
  manager:
    'Sees their team\'s attendance and customers, and can add salesmen. '
    + 'Cannot change the account or make anyone an admin.',
  sales_rep:
    'Works in the field on the phone app: marks attendance and adds customers. '
    + 'Signs in with their mobile number and an OTP — no password to forget.',
  delivery:
    'Delivers orders on the phone app: sees the day\'s dispatch run, captures a '
    + 'photo and signature on each drop. Signs in with their mobile number and an OTP.',
  distributor:
    'An intermediary between your company and your retailers. Receives secondary '
    + 'orders, places primary orders, and manages their own retailer relationships. '
    + 'Signs in with an email address and password.',
};

/** The roles the "add someone" flow offers, in the order the user asked for. */
export const CREATABLE_ROLES: Role[] = ['company_admin', 'manager', 'sales_rep', 'delivery', 'distributor'];

export const STATUS_LABEL: Record<UserStatus, string> = {
  invited: 'Not joined yet',
  pending_verification: 'Verifying',
  active: 'Active',
  suspended: 'Suspended',
  deactivated: 'Removed',
};

export const STATUS_TONE: Record<UserStatus, 'success' | 'warning' | 'neutral' | 'danger'> = {
  invited: 'warning',
  pending_verification: 'warning',
  active: 'success',
  suspended: 'danger',
  deactivated: 'neutral',
};

export const isAdminRole = (role: Role) =>
  role === 'company_admin' || role === 'platform_admin';

/**
 * Which contact detail a role cannot do without.
 *
 * This is not decoration: an admin with no email can never sign in to
 * the console, and a salesman with no mobile can never sign in to the
 * app. A distributor needs BOTH — email to sign in, mobile for the
 * business relationship. The API refuses both; the form says so before
 * they try.
 */
export function requiredContact(role: Role): 'email' | 'mobile' | 'both' {
  if (role === 'distributor') return 'both';
  return isAdminRole(role) ? 'email' : 'mobile';
}

export function initialsOf(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
